import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useProfessors } from '@/features/director-professors/hooks/useProfessors';
import { useUpdateClass, useRemoveClass } from '../hooks/useSchedules';
/**
 * Calendar cell card for a single class. Three modes:
 *   - Display: shows time + subject + professor. Draggable.
 *   - Edit: tiny inline form with start/end time + professor select.
 *   - Locked: display only, no drag/edit/delete (used for published schedules).
 */
export function ScheduleClassCard({ cls, scheduleId, oppositionId, locked }) {
    const updateClass = useUpdateClass(scheduleId);
    const removeClass = useRemoveClass(scheduleId);
    const { data: professors = [] } = useProfessors();
    const [editing, setEditing] = useState(false);
    const [draftStart, setDraftStart] = useState(cls.start_time.slice(0, 5));
    const [draftEnd, setDraftEnd] = useState(cls.end_time.slice(0, 5));
    const [draftProfessor, setDraftProfessor] = useState(cls.professor_id);
    const [error, setError] = useState(null);
    // Reset draft state if the class itself updates from the server.
    useEffect(() => {
        if (!editing) {
            setDraftStart(cls.start_time.slice(0, 5));
            setDraftEnd(cls.end_time.slice(0, 5));
            setDraftProfessor(cls.professor_id);
            setError(null);
        }
    }, [editing, cls.start_time, cls.end_time, cls.professor_id]);
    // Eligible professors: same opposition + teach this subject (matches the
    // filter in AddClassForm so the create + edit flows agree).
    const eligibleProfessors = useMemo(() => professors.filter((p) => p.opposition_id === oppositionId &&
        p.subjects.some((s) => s.id === cls.subject_id)), [professors, oppositionId, cls.subject_id]);
    // If the current professor isn't in the eligible list (data drift), surface
    // them anyway so the director can see what's currently saved.
    const professorOptions = useMemo(() => {
        const arr = [...eligibleProfessors];
        if (!arr.some((p) => p.user_profile_id === cls.professor_id)) {
            arr.unshift({
                id: '__current',
                user_profile_id: cls.professor_id,
                opposition_id: oppositionId,
                first_name: cls.professor_name.split(' ')[0] ?? cls.professor_name,
                last_name: cls.professor_name.split(' ').slice(1).join(' '),
                email: '',
                phone: null,
                bio: null,
                subjects: [],
                can_collect_payments: false,
                created_at: '',
                updated_at: '',
            });
        }
        return arr;
    }, [eligibleProfessors, cls.professor_id, cls.professor_name, oppositionId]);
    function handleSave() {
        const dto = {};
        if (draftStart !== cls.start_time.slice(0, 5))
            dto.start_time = draftStart;
        if (draftEnd !== cls.end_time.slice(0, 5))
            dto.end_time = draftEnd;
        if (draftProfessor !== cls.professor_id)
            dto.professor_id = draftProfessor;
        if (Object.keys(dto).length === 0) {
            setEditing(false);
            return;
        }
        if (dto.start_time && dto.end_time && dto.end_time <= dto.start_time) {
            setError('La hora de fin debe ser posterior.');
            return;
        }
        setError(null);
        updateClass.mutate({ classId: cls.id, ...dto }, {
            onSuccess: () => setEditing(false),
            onError: (err) => {
                const message = err instanceof Error ? err.message : 'Error al guardar';
                setError(message);
            },
        });
    }
    if (editing) {
        return (_jsxs("div", { className: "rounded border border-[var(--accent)] bg-white p-1 dark:bg-gray-900", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex gap-1", children: [_jsx("input", { type: "time", value: draftStart, onChange: (e) => setDraftStart(e.target.value), className: "w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-[10px] dark:border-gray-700 dark:bg-gray-800" }), _jsx("input", { type: "time", value: draftEnd, onChange: (e) => setDraftEnd(e.target.value), className: "w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-[10px] dark:border-gray-700 dark:bg-gray-800" })] }), _jsx("select", { value: draftProfessor, onChange: (e) => setDraftProfessor(e.target.value), className: "mt-1 w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-[10px] dark:border-gray-700 dark:bg-gray-800", children: professorOptions.map((p) => (_jsxs("option", { value: p.user_profile_id, children: [p.first_name, " ", p.last_name] }, p.user_profile_id))) }), error && _jsx("p", { className: "mt-0.5 text-[9px] text-red-600", children: error }), _jsxs("div", { className: "mt-1 flex gap-1", children: [_jsx("button", { type: "button", onClick: handleSave, disabled: updateClass.isPending, className: "flex-1 rounded bg-[var(--accent)] px-1 py-0.5 text-[10px] font-medium text-white disabled:opacity-50", children: updateClass.isPending ? '…' : 'Guardar' }), _jsx("button", { type: "button", onClick: () => setEditing(false), className: "flex-1 rounded border border-gray-300 px-1 py-0.5 text-[10px] dark:border-gray-700", children: "Cancelar" })] })] }));
    }
    return (_jsxs("div", { draggable: !locked, onDragStart: (e) => {
            e.dataTransfer.setData('text/plain', cls.id);
            e.dataTransfer.effectAllowed = 'move';
        }, onClick: (e) => {
            e.stopPropagation();
            if (!locked)
                setEditing(true);
        }, className: `group relative rounded bg-blue-100 px-1 py-0.5 dark:bg-blue-900/30 ${locked ? '' : 'cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/50'}`, title: `${cls.subject_name} · ${cls.professor_name}\n${cls.start_time.slice(0, 5)} – ${cls.end_time.slice(0, 5)}${cls.classroom ? ` · ${cls.classroom}` : ''}`, children: [_jsxs("p", { className: "truncate text-xs font-semibold text-blue-800 dark:text-blue-300", children: [cls.start_time.slice(0, 5), " ", cls.subject_name] }), _jsx("p", { className: "truncate text-[10px] text-blue-700/80 dark:text-blue-300/70", children: cls.professor_name }), !locked && (_jsx("button", { onClick: (e) => {
                    e.stopPropagation();
                    removeClass.mutate(cls.id);
                }, className: "absolute right-0.5 top-0.5 hidden text-red-500 hover:text-red-700 group-hover:block", title: "Eliminar clase", "aria-label": "Eliminar clase", children: "\u00D7" }))] }));
}
//# sourceMappingURL=ScheduleClassCard.js.map